/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import React from 'react';
import PropTypes from 'prop-types';

import styles from './TopicTable.module.scss';

/**
 * This is the TopicSum component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function TopicSum({ title, view, filterYears, labels, points }) {
  // Calculate the number of year columns to show, based on the year filter
  const yearColumns = filterYears[1] - filterYears[0] + 1;

  // Generate array of the relevant years
  const yearArray = Array(filterYears[1] - filterYears[0] + 1)
    .fill()
    .map((_, idx) => filterYears[0] + idx);

  function renderDesktopTable() {
    return (
      <table className={styles.table}>
        <caption>
          <div className={styles.title}>
            <h3>{title}</h3>
          </div>
        </caption>
        <thead>
          <tr>
            <td className={styles.code}></td>
            <td className={styles.name}></td>
            {yearArray.map((y) => (
              <th key={y} className={styles.points}>
                {y}. {labels.year}
              </th>
            ))}
            <th className={styles.total}>{labels.total}</th>
          </tr>
        </thead>
        <tbody>
          <tr className={styles.sum}>
            <th className={styles.code}>{labels.sum}</th>
            <td className={styles.name}>&nbsp;</td>
            {yearArray.map((y) => (
              <td className={styles.points} key={y}>
                {points[y] ?? ''}
              </td>
            ))}
            <td className={styles.total}>
              {Object.values(points).reduce((c, p) => c + p, 0)}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  function renderMobileTable() {
    return (
      <table className={styles.table}>
        <caption>
          <div className={styles.title}>
            <h3>{title}</h3>
          </div>
        </caption>
        <colgroup>
          <col />
          <col span={yearColumns} />
        </colgroup>
        <thead>
          <tr>
            <th className={styles.name}></th>
            {yearArray.map((y) => (
              <th key={y} className={styles.points}>
                {y}. {labels.year}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className={styles.sum}>
            <th className={styles.code}>{labels.sum}</th>
            {yearArray.map((y) => (
              <td className={styles.points} key={y}>
                {points[y] ?? ''}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <div className={styles.main}>
      {view === 'desktop' && renderDesktopTable()}
      {view === 'mobile' && renderMobileTable()}
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
TopicSum.propTypes = {
  title: PropTypes.string,
  filterYears: PropTypes.array,
  view: PropTypes.oneOf(['desktop', 'mobile']),
  points: PropTypes.arrayOf(PropTypes.object),
  labels: PropTypes.shape({
    btnExpand: PropTypes.string,
    btnPullTogether: PropTypes.string,
    code: PropTypes.string,
    topic: PropTypes.string,
    pointsPerYear: PropTypes.string,
    pointsPerYearShort: PropTypes.string,
    total: PropTypes.string,
    year: PropTypes.string,
    empty: PropTypes.string,
    sum: PropTypes.string,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
TopicSum.defaultProps = {
  title: '',
  filterYears: [1, 4],
  view: 'desktop',
  points: [],
};

export default TopicSum;
